/**
 * The fields of one template item (plan docs/plans/lms-plan.md 12.1, 5.7;
 * WP-47; R14): the title, the kind (with what it covers), who is responsible,
 * the evidence, the due day counted from the first day, the link, the
 * description (the Learning Center markdown subset), the linked course (a
 * course item's course, or the course an external training's certificate is
 * offered against; WP-43's CoursePicker, drafts included because a template
 * may link a course before it is published, 18.24) and the verification
 * flag. Each field shows its own problem (model.ts itemProblems).
 */
import FilterSelect from '../../../../ui/FilterSelect';
import { fieldCls } from '../../../../ui/styles';
import { COURSE_LINK_KINDS, ONBOARDING_ITEM_KINDS, ONBOARDING_LIMITS } from '../../../api/onboarding';
import type { OnboardingEvidence, OnboardingItemKind, OnboardingResponsible } from '../../../api/onboarding';
import { CoursePicker } from '../../../components/pickers/CoursePicker';
import { checkboxCls, labelCls, metaCls } from '../../../components/pickers/shared';
import { EVIDENCE_LABELS, KIND_HINTS, KIND_LABELS, RESPONSIBLE_LABELS } from '../../onboarding/shared';
import { withKind } from './model';
import type { DraftItem, DraftProblems } from './model';

export interface ItemFormProps {
  draft: DraftItem;
  problems: DraftProblems;
  onChange: (next: DraftItem) => void;
}

const inputCls = `${fieldCls} min-h-[44px] w-full sm:min-h-0`;

function Problem({ text, testId }: { text?: string; testId: string }) {
  if (!text) return null;
  return (
    <p className="mt-1 text-xs text-red-700 dark:text-red-400" data-testid={testId}>
      {text}
    </p>
  );
}

export function ItemForm({ draft, problems, onChange }: ItemFormProps) {
  const k = draft.key;
  const id = (field: string) => `lms-template-item-${k}-${field}`;
  const set = (patch: Partial<DraftItem>) => onChange({ ...draft, ...patch });
  const linksCourse = COURSE_LINK_KINDS.includes(draft.kind);

  return (
    <div className="grid gap-3 border-t border-gray-100 pt-3 dark:border-gray-700 md:grid-cols-2" data-testid={`template-item-form-${k}`}>
      <div className="md:col-span-2">
        <label htmlFor={id('title')} className={labelCls}>
          Title
        </label>
        <input
          id={id('title')}
          className={inputCls}
          value={draft.title}
          maxLength={ONBOARDING_LIMITS.itemTitle + 20}
          onChange={(e) => set({ title: e.target.value })}
          data-testid={`template-item-title-${k}`}
        />
        <Problem text={problems.title} testId={`template-item-problem-title-${k}`} />
      </div>

      <div data-testid={`template-item-kind-${k}`}>
        <div className={labelCls} aria-hidden="true">
          Kind
        </div>
        <FilterSelect value={draft.kind} onChange={(v) => onChange(withKind(draft, v as OnboardingItemKind))} label="Kind">
          {ONBOARDING_ITEM_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {KIND_LABELS[kind]}
            </option>
          ))}
        </FilterSelect>
        <p className={`${metaCls} mt-1`}>{KIND_HINTS[draft.kind]}</p>
      </div>

      <div data-testid={`template-item-responsible-${k}`}>
        <div className={labelCls} aria-hidden="true">
          Who does it
        </div>
        <FilterSelect
          value={draft.responsible}
          onChange={(v) => set({ responsible: v as OnboardingResponsible })}
          label="Who does it"
          disabled={draft.kind === 'course'}
        >
          {(['new_hire', 'admin'] as OnboardingResponsible[]).map((r) => (
            <option key={r} value={r}>
              {RESPONSIBLE_LABELS[r]}
            </option>
          ))}
        </FilterSelect>
        {draft.kind === 'course' ? <p className={`${metaCls} mt-1`}>{"A course item is the new team member's: the course is assigned to them."}</p> : null}
        <Problem text={problems.responsible} testId={`template-item-problem-responsible-${k}`} />
      </div>

      <div data-testid={`template-item-evidence-${k}`}>
        <div className={labelCls} aria-hidden="true">
          Evidence
        </div>
        <FilterSelect value={draft.evidence} onChange={(v) => set({ evidence: v as OnboardingEvidence })} label="Evidence">
          {(['none', 'text', 'file'] as OnboardingEvidence[]).map((e) => (
            <option key={e} value={e}>
              {EVIDENCE_LABELS[e]}
            </option>
          ))}
        </FilterSelect>
      </div>

      <div>
        <label htmlFor={id('due')} className={labelCls}>
          Due (days after the first day)
        </label>
        <input
          id={id('due')}
          className={inputCls}
          inputMode="numeric"
          value={draft.due_days}
          onChange={(e) => set({ due_days: e.target.value })}
          data-testid={`template-item-due-${k}`}
        />
        <Problem text={problems.due_days} testId={`template-item-problem-due-${k}`} />
      </div>

      <div className="md:col-span-2">
        <label htmlFor={id('url')} className={labelCls}>
          Link <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
        </label>
        <input
          id={id('url')}
          className={inputCls}
          type="url"
          placeholder="https://"
          value={draft.url}
          onChange={(e) => set({ url: e.target.value })}
          data-testid={`template-item-url-${k}`}
        />
        <p className={`${metaCls} mt-1`}>The site to visit. Never a link that carries a login or a password.</p>
        <Problem text={problems.url} testId={`template-item-problem-url-${k}`} />
      </div>

      <div className="md:col-span-2">
        <label htmlFor={id('description')} className={labelCls}>
          Description <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
        </label>
        <textarea
          id={id('description')}
          className={`${fieldCls} w-full`}
          rows={3}
          value={draft.description_md}
          onChange={(e) => set({ description_md: e.target.value })}
          data-testid={`template-item-description-${k}`}
        />
        <p className={`${metaCls} mt-1`}>Plain text, or **bold**, *italic* and [links](https://example.org). No passwords or logins.</p>
        <Problem text={problems.description_md} testId={`template-item-problem-description-${k}`} />
      </div>

      {linksCourse ? (
        <div className="md:col-span-2">
          <CoursePicker
            value={draft.course_id}
            onChange={(courseId) => set({ course_id: courseId })}
            publishedOnly={false}
            label={draft.kind === 'course' ? 'Course' : 'Linked course (optional)'}
          />
          <p className={`${metaCls} mt-1`}>
            {draft.kind === 'course'
              ? 'Finishing this course completes the item. Onboarding cannot start while the course is unpublished or retired.'
              : 'The certificate the training produces is offered to an admin as an external certificate for this course.'}
          </p>
          <Problem text={problems.course_id} testId={`template-item-problem-course-${k}`} />
        </div>
      ) : null}

      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-gray-800 dark:text-gray-200 md:col-span-2 sm:min-h-0">
        <input
          type="checkbox"
          className={checkboxCls}
          checked={draft.requires_verification}
          onChange={(e) => set({ requires_verification: e.target.checked })}
          data-testid={`template-item-verify-${k}`}
        />
        An admin verifies it after it is marked done
      </label>
    </div>
  );
}

export default ItemForm;
